import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { applyFormat } from '../../../core/directives/vivest-control';
import { ResumoResponse } from '../../../core/models/resumo';
import { Steps } from '../../../shared/components/steps/steps';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { ToastService } from '../../../shared/services/toast-service';
import { formatarImpostoComAliquota, formatarMoeda, formatarValor } from '../../../shared/utils/formatadores';

/**
 * Formata o CPF recebido como texto (000.000.000-00). Aceita só dígitos, já mascarado ou sem os
 * zeros à esquerda (ex.: "603733859" → "006.037.338-59").
 */
function formatarCpf(cpf: string | null | undefined): string {
  const digitos = (cpf ?? '').replace(/\D/g, '');
  return digitos ? applyFormat(digitos.padStart(11, '0'), 'cpf') : '';
}

@Component({
  imports: [Steps],
  selector: 'app-resumo',
  styleUrl: './resumo.scss',
  templateUrl: './resumo.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Resumo implements OnInit {
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(6);
  readonly totalSteps = signal(7);
  readonly resumo = signal<ResumoResponse | null>(null);

  /** Valores já formatados para exibição; nulo enquanto a API não responde. */
  protected readonly exibicao = computed(() => {
    const resumo = this.resumo();
    if (!resumo) return null;

    return {
      nome: resumo.nome,
      cpf: formatarCpf(resumo.cpf),
      matricula: resumo.matricula,
      opcao: resumo.opcao,
      regime: resumo.impostoRenda,
      banco: resumo.bancoNome,
      agencia: resumo.agencia,
      conta: resumo.conta,
      numeroParcelas: resumo.resgateNumeroParcelas,
      valorPrimeiraParcela: formatarMoeda(resumo.resgateValorPrimeiraParcela),
      valorBruto: formatarMoeda(resumo.resgateValorBruto),
      valorIR: formatarImpostoComAliquota(resumo.resgateValorImpostoRenda, resumo.resgateValorAliquotaEfetiva),
      // Só o número: na última linha o "R$" fica num span com tamanho menor.
      valorLiquido: formatarValor(resumo.resgateValorLiquidoEstimado),
    };
  });

  ngOnInit(): void {
    void this.carregarResumo();
  }

  protected async carregarResumo(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getResumo();
      this.resumo.set(body);
    } catch {
      this.toastService.show('Não foi possível carregar as informações. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  onReturn() {
    this.router.navigate(['/instituto-online/upload-documentos']);
  }

  /** Confirma o resumo e, com sucesso, segue para a assinatura do termo. */
  async onSubmit(): Promise<void> {
    this.loaderService.showLoader('Salvando...');

    try {
      await this.httpClientService.saveResumo();
      this.router.navigate(['/instituto-online/assinatura-termo']);
    } catch {
      this.toastService.show('Não foi possível salvar a revisão. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }
}
